/**
 * Admin dashboard: how many members there are and how much they write.
 * Visible only to admins (the API refuses everyone else with 403).
 */

import { useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw, Search } from 'lucide-react';
import { useAdminOverviewQuery, useAdminUsersQuery, useSetPlusMutation, type AdminUserRow } from './api';
import { AdminSettings } from './AdminSettings';
import { AdminReferrals } from './AdminReferrals';
import { AdminAnnouncements } from './AdminAnnouncements';
import { AdminPostage } from './AdminPostage';
import { AdminStamps } from './AdminStamps';
import { useAppSettings } from '../settings/api';

type SortKey =
  | 'email'
  | 'signedUpAt'
  | 'lastActiveAt'
  | 'relationships'
  | 'lettersWritten'
  | 'lettersReceived'
  | 'reads'
  | 'unopened';

const COLUMNS: { key: SortKey; label: string; numeric?: boolean; title?: string }[] = [
  { key: 'email', label: 'Member' },
  { key: 'signedUpAt', label: 'Signed up' },
  { key: 'lastActiveAt', label: 'Last active', title: 'Most recent session activity' },
  { key: 'relationships', label: 'Relationships', numeric: true },
  { key: 'lettersWritten', label: 'Written', numeric: true, title: 'Letters sealed and sent' },
  { key: 'lettersReceived', label: 'Received', numeric: true, title: 'Letters that have arrived' },
  { key: 'reads', label: 'Reads', numeric: true, title: 'Times they opened letters sent to them' },
  { key: 'unopened', label: 'Unopened', numeric: true, title: 'Arrived letters still waiting' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

function pct(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';
}

function Stat({ label, value, note }: { label: string; value: number | string; note?: string }) {
  return (
    <div className="admin-stat">
      <span className="admin-stat__value">{value}</span>
      <span className="admin-stat__label">{label}</span>
      {note && <span className="admin-stat__note">{note}</span>}
    </div>
  );
}

function isPlusNow(plusUntil: string | null): boolean {
  return plusUntil != null && new Date(plusUntil).getTime() > Date.now();
}

/** Plus on/off for one member. On means "until revoked" while there are no payments. */
function PlusToggle({
  on,
  disabled,
  email,
  onChange,
}: {
  on: boolean;
  disabled: boolean;
  email: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`toiyp Plus for ${email}`}
      className={`admin-switch${on ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="admin-switch__knob" aria-hidden="true" />
    </button>
  );
}

export function AdminScreen({ onBack }: { onBack: () => void }) {
  const overview = useAdminOverviewQuery();
  const users = useAdminUsersQuery();
  const [tab, setTab] = useState<
    'members' | 'postage' | 'stamps' | 'referrals' | 'announcements' | 'controls'
  >('members');
  const { postageEnabled } = useAppSettings();
  const [setPlus, { isLoading: savingPlus }] = useSetPlusMutation();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'signedUpAt', desc: true });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (users.data ?? []).filter(
      (u) =>
        !q ||
        u.email.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q) ||
        (u.handle ?? '').toLowerCase().includes(q),
    );
    const val = (u: AdminUserRow) => {
      const v = u[sort.key];
      if (v == null) return -Infinity;
      return typeof v === 'string' && sort.key !== 'email' ? new Date(v).getTime() : v;
    };
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return sort.desc ? -c : c;
    });
  }, [users.data, query, sort]);

  const o = overview.data;
  const refresh = () => {
    void overview.refetch();
    void users.refetch();
  };
  const forbidden =
    (overview.error && 'status' in overview.error && overview.error.status === 403) ||
    (users.error && 'status' in users.error && users.error.status === 403);

  return (
    <section className="screen" aria-label="Admin dashboard">
      <header className="screen__header">
        <button type="button" className="screen__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <span className="screen__title">Admin</span>
        <button
          type="button"
          className="icon-btn"
          onClick={refresh}
          aria-label="Refresh"
          title="Refresh"
          disabled={overview.isFetching || users.isFetching}
        >
          <RefreshCw size={16} />
        </button>
      </header>

      <div className="admin">
        <div className="admin-tabs" role="tablist" aria-label="Admin sections">
          {(['members', 'postage', 'stamps', 'referrals', 'announcements', 'controls'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`admin-tabs__tab${tab === t ? ' is-on' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'members'
                ? 'Members'
                : t === 'postage'
                  ? 'Postage'
                  : t === 'stamps'
                    ? 'Stamps & stationery'
                  : t === 'referrals'
                    ? 'Referrals'
                    : t === 'announcements'
                      ? 'Post Office'
                      : 'App controls'}
            </button>
          ))}
        </div>
        {forbidden ? (
          <p className="admin__empty">This page is for admins only.</p>
        ) : tab === 'controls' ? (
          <AdminSettings />
        ) : tab === 'postage' ? (
          <AdminPostage enabled={postageEnabled} />
        ) : tab === 'stamps' ? (
          <AdminStamps members={users.data ?? []} />
        ) : tab === 'referrals' ? (
          <AdminReferrals />
        ) : tab === 'announcements' ? (
          <AdminAnnouncements />
        ) : overview.error || users.error ? (
          <p className="admin__empty">Couldn’t load the numbers. Try refreshing.</p>
        ) : (
          <>
            <div className="admin-stats">
              <Stat
                label="Members"
                value={o?.users ?? '…'}
                note={o ? `+${o.signups7d} this week · +${o.signups30d} in 30 days` : undefined}
              />
              <Stat
                label="Active this week"
                value={o?.active7d ?? '…'}
                note={o ? `${pct(o.active7d, o.users)} of members` : undefined}
              />
              <Stat
                label="Letters sent"
                value={o?.lettersSent ?? '…'}
                note={o ? `${o.lettersSent7d} this week · ${o.lettersInFlight} in the post` : undefined}
              />
              <Stat
                label="Letters opened"
                value={o?.lettersOpened ?? '…'}
                note={o ? `${pct(o.lettersOpened, o.lettersSent)} of sent` : undefined}
              />
              <Stat
                label="Relationships"
                value={o?.activeRelationships ?? '…'}
                note={o ? `${o.pendingRelationships} pending` : undefined}
              />
              <Stat
                label="Invited, not joined"
                value={o?.pendingInvitees ?? '…'}
                note="written to by email"
              />
              <Stat
                label="Push enabled"
                value={o?.pushEnabledUsers ?? '…'}
                note={o ? `${pct(o.pushEnabledUsers, o.users)} of members` : undefined}
              />
            </div>

            <div className="admin__toolbar">
              <label className="admin__search">
                <Search size={14} aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search email, name or handle"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search members"
                />
              </label>
              <span className="admin__count">
                {users.data ? `${rows.length} of ${users.data.length}` : 'loading…'}
              </span>
            </div>

            <div className="admin-table__wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className={c.numeric ? 'is-num' : undefined}
                        title={c.title}
                        aria-sort={
                          sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : undefined
                        }
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setSort((s) =>
                              s.key === c.key ? { key: c.key, desc: !s.desc } : { key: c.key, desc: c.key !== 'email' },
                            )
                          }
                        >
                          {c.label}
                          {sort.key === c.key ? (sort.desc ? ' ↓' : ' ↑') : ''}
                        </button>
                      </th>
                    ))}
                    <th title="toiyp Plus — craft perks only. Admin-granted until payments exist.">Plus</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span className="admin-table__email">{u.email}</span>
                        <span className="admin-table__sub">
                          {u.name}
                          {u.handle ? ` · @${u.handle}` : ''}
                          {u.pushEnabled ? ' · push' : ''}
                        </span>
                      </td>
                      <td>{fmtDate(u.signedUpAt)}</td>
                      <td>
                        {ago(u.lastActiveAt)}
                        {u.lastWroteAt && (
                          <span className="admin-table__sub">wrote {ago(u.lastWroteAt)}</span>
                        )}
                      </td>
                      <td className="is-num">{u.relationships}</td>
                      <td className="is-num">{u.lettersWritten}</td>
                      <td className="is-num">{u.lettersReceived}</td>
                      <td className="is-num">{u.reads}</td>
                      <td className="is-num">{u.unopened}</td>
                      <td>
                        <PlusToggle
                          on={isPlusNow(u.plusUntil)}
                          disabled={savingPlus}
                          email={u.email}
                          onChange={(plus) => void setPlus({ userId: u.id, plus })}
                        />
                      </td>
                    </tr>
                  ))}
                  {users.data && rows.length === 0 && (
                    <tr>
                      <td colSpan={COLUMNS.length + 1} className="admin__empty">
                        No members match.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
